// 2D top-down editor — Konva stage with the uploaded plan, metric grid,
// calibration line, traced walls and draggable/rotatable furniture.
// Touch-ready: pinch to zoom, one-finger pan on empty background, drag items.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Stage, Layer, Image as KImage, Line, Rect, Circle, Arc, Text as KText, Group, Transformer } from "react-konva";
import { Plus, Minus, Maximize2 } from "lucide-react";
import { collides, fmtM } from "./data";

function usePlanImage(dataUrl) {
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!dataUrl) { setImg(null); return; }
    const im = new window.Image();
    im.onload = () => setImg(im);
    im.src = dataUrl;
  }, [dataUrl]);
  return img;
}

/** Loads the real product photos for placed items (same-origin proxy, so the
 * canvas stays untainted for PDF export). Keyed by image URL. */
function useItemImages(items, imgByRef) {
  const [map, setMap] = useState({});
  const requested = useRef(new Set());
  const urls = useMemo(
    () => [...new Set(items.map((i) => i.img || imgByRef?.[i.ref]).filter(Boolean))],
    [items, imgByRef]
  );
  useEffect(() => {
    urls.forEach((u) => {
      if (requested.current.has(u)) return;
      requested.current.add(u);
      const im = new window.Image();
      im.onload = () => setMap((m) => ({ ...m, [u]: im }));
      im.onerror = () => setMap((m) => ({ ...m, [u]: null }));
      im.src = u;
    });
  }, [urls]);
  return map;
}

export default function EditorCanvas({
  plan, mpp, items, setItems, walls, setWalls, tool, placing, onPlace,
  calibLine, setCalibLine, bgOpacity, selected, setSelected, stageRef, containerRef, brand, imgByRef, openingType,
}) {
  const img = usePlanImage(plan?.dataUrl);
  const itemImages = useItemImages(items, imgByRef);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const trRef = useRef(null);
  const shapeRefs = useRef({});
  const [draftWall, setDraftWall] = useState([]);
  const [mousePt, setMousePt] = useState(null);
  // user zoom/pan on top of the fit transform
  const [zoom, setZoom] = useState({ s: 1, x: 0, y: 0 });
  const gesture = useRef({ lastDist: 0, panning: false, lx: 0, ly: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [containerRef]);

  // Reset zoom when a new plan image loads
  useEffect(() => { setZoom({ s: 1, x: 0, y: 0 }); }, [plan?.dataUrl]);

  // Fit the plan image into the container (image px coordinate space preserved).
  const fit = useMemo(() => {
    if (!img) return { s: 1, x: 0, y: 0 };
    const s = Math.min(size.w / img.width, size.h / img.height) * 0.98;
    return { s, x: (size.w - img.width * s) / 2, y: (size.h - img.height * s) / 2 };
  }, [img, size]);

  // Absolute stage transform = fit composed with user zoom/pan
  const tr = { s: fit.s * zoom.s, x: fit.x * zoom.s + zoom.x, y: fit.y * zoom.s + zoom.y };

  const applyZoomAt = (cx, cy, k) => {
    setZoom((z) => {
      const S = fit.s * z.s, X = fit.x * z.s + z.x, Y = fit.y * z.s + z.y;
      const S2 = Math.max(fit.s * 0.5, Math.min(fit.s * 14, S * k));
      if (S2 === S) return z;
      const r = S2 / S;
      return {
        s: S2 / fit.s,
        x: cx - (cx - X) * r - fit.x * (S2 / fit.s),
        y: cy - (cy - Y) * r - fit.y * (S2 / fit.s),
      };
    });
  };

  useEffect(() => {
    if (trRef.current && selected && shapeRefs.current[selected]) {
      trRef.current.nodes([shapeRefs.current[selected]]);
      trRef.current.getLayer()?.batchDraw();
    } else if (trRef.current) {
      trRef.current.nodes([]);
    }
  }, [selected, items]);

  useEffect(() => { setDraftWall([]); }, [tool]);

  // collision map (only when calibrated — px sizes are meaningful anyway)
  const colliding = useMemo(() => {
    const bad = new Set();
    for (let i = 0; i < items.length; i++)
      for (let j = i + 1; j < items.length; j++)
        if (collides(items[i], items[j])) { bad.add(items[i].uid); bad.add(items[j].uid); }
    return bad;
  }, [items]);

  const toImagePx = (pt) => ({ x: (pt.x - tr.x) / tr.s, y: (pt.y - tr.y) / tr.s });

  // Door/window placement: snap to the nearest wall segment, insert an opening
  const placeOpening = (pt) => {
    if (!mpp) return; // the banner tells the user to calibrate first
    const wPx = (openingType === "door" ? 0.83 : 1.0) / mpp;
    let best = null;
    walls.forEach((w) => {
      const p = w.points;
      for (let i = 0; i + 3 < p.length; i += 2) {
        const ax = p[i], ay = p[i + 1], bx = p[i + 2], by = p[i + 3];
        const dx = bx - ax, dy = by - ay;
        const len2 = dx * dx + dy * dy || 1;
        const segLen = Math.sqrt(len2);
        const t = Math.max(0, Math.min(1, ((pt.x - ax) * dx + (pt.y - ay) * dy) / len2));
        const cx = ax + t * dx, cy = ay + t * dy;
        const dist = Math.hypot(pt.x - cx, pt.y - cy);
        if (!best || dist < best.dist) best = { wallId: w.id, seg: i / 2, dist, off: t * segLen, segLen };
      }
    });
    if (!best || best.dist > 24 / tr.s) return; // clicked too far from any wall
    if (best.segLen < wPx + 6 / tr.s) return;   // segment too short for this opening
    const off = Math.max(wPx / 2, Math.min(best.segLen - wPx / 2, best.off));
    setWalls((arr) => arr.map((w) => (w.id === best.wallId
      ? { ...w, openings: [...(w.openings || []), { id: `o${Date.now().toString(36)}${Math.floor(Math.random() * 99)}`, seg: best.seg, off, wPx, type: openingType }] }
      : w)));
  };

  const onMouseDown = (e) => {
    const stage = e.target.getStage();
    const sp = stage.getPointerPosition();
    const pt = toImagePx(sp);
    if (tool === "calibrate") setCalibLine({ pts: [pt.x, pt.y, pt.x, pt.y] });
    else if (tool === "wall") setDraftWall((p) => [...p, pt.x, pt.y]);
    else if (tool === "opening") { placeOpening(pt); return; }
    else if (tool === "place" && placing) { onPlace(pt); return; }
    else if (e.target === stage || e.target.name() === "plan-bg") {
      setSelected(null);
      gesture.current.panning = true;
      gesture.current.lx = sp.x; gesture.current.ly = sp.y;
    }
  };

  const onMouseMove = (e) => {
    const stage = e.target.getStage();
    const sp = stage.getPointerPosition();
    if (gesture.current.panning) {
      const dx = sp.x - gesture.current.lx, dy = sp.y - gesture.current.ly;
      gesture.current.lx = sp.x; gesture.current.ly = sp.y;
      setZoom((z) => ({ ...z, x: z.x + dx, y: z.y + dy }));
      return;
    }
    const pt = toImagePx(sp);
    setMousePt(pt);
    if (tool === "calibrate" && calibLine)
      setCalibLine({ pts: [calibLine.pts[0], calibLine.pts[1], pt.x, pt.y] });
  };

  const endGesture = () => { gesture.current.panning = false; gesture.current.lastDist = 0; };

  const getTouchPts = (e) => {
    const rect = e.target.getStage().container().getBoundingClientRect();
    return [...e.evt.touches].map((t) => ({ x: t.clientX - rect.left, y: t.clientY - rect.top }));
  };

  const onTouchStart = (e) => {
    if (e.evt.touches.length === 2) {
      gesture.current.panning = false;
      const [a, b] = getTouchPts(e);
      gesture.current.lastDist = Math.hypot(a.x - b.x, a.y - b.y);
      return;
    }
    onMouseDown(e);
  };

  const onTouchMove = (e) => {
    e.evt.preventDefault();
    if (e.evt.touches.length === 2) {
      const [a, b] = getTouchPts(e);
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const c = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      if (gesture.current.lastDist > 0) applyZoomAt(c.x, c.y, d / gesture.current.lastDist);
      gesture.current.lastDist = d;
      return;
    }
    onMouseMove(e);
  };

  const onWheel = (e) => {
    e.evt.preventDefault();
    const p = e.target.getStage().getPointerPosition();
    applyZoomAt(p.x, p.y, e.evt.deltaY > 0 ? 1 / 1.12 : 1.12);
  };

  const finishWall = () => {
    if (draftWall.length >= 4) setWalls((w) => [...w, { id: `w${Date.now()}`, points: draftWall }]);
    setDraftWall([]);
  };

  const onDblClick = () => { if (tool === "wall") finishWall(); };

  const gridPx = mpp ? 1 / mpp : 0; // px per 1 m
  const wallStroke = mpp ? 0.15 / mpp : 10;

  const gridLines = useMemo(() => {
    if (!img || !gridPx) return [];
    const lines = [];
    const stepMinor = gridPx / 2;
    for (let x = 0; x <= img.width; x += stepMinor)
      lines.push({ pts: [x, 0, x, img.height], major: Math.round(x / stepMinor) % 2 === 0 });
    for (let y = 0; y <= img.height; y += stepMinor)
      lines.push({ pts: [0, y, img.width, y], major: Math.round(y / stepMinor) % 2 === 0 });
    return lines;
  }, [img, gridPx]);

  const calibLenPx = calibLine
    ? Math.hypot(calibLine.pts[2] - calibLine.pts[0], calibLine.pts[3] - calibLine.pts[1])
    : 0;

  // Split each wall segment around its openings; returns plain line pieces plus
  // opening descriptors with their geometry for drawing the symbols.
  const wallPieces = (w) => {
    const out = [];
    const p = w.points;
    for (let i = 0; i + 3 < p.length; i += 2) {
      const ax = p[i], ay = p[i + 1], bx = p[i + 2], by = p[i + 3];
      const dx = bx - ax, dy = by - ay;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len, uy = dy / len;
      const ops = (w.openings || []).filter((o) => o.seg === i / 2).sort((a2, b2) => a2.off - b2.off);
      if (!ops.length) { out.push({ pts: [ax, ay, bx, by] }); continue; }
      let cur = 0;
      ops.forEach((o) => {
        const s = Math.max(0, o.off - o.wPx / 2), e = Math.min(len, o.off + o.wPx / 2);
        if (s > cur) out.push({ pts: [ax + ux * cur, ay + uy * cur, ax + ux * s, ay + uy * s] });
        out.push({ opening: o, ax, ay, ux, uy, s, e, deg: (Math.atan2(dy, dx) * 180) / Math.PI });
        cur = e;
      });
      if (cur < len) out.push({ pts: [ax + ux * cur, ay + uy * cur, bx, by] });
    }
    return out;
  };

  const zoomBtn = "p-2.5 transition hover:opacity-75";

  return (
    <>
      <Stage
        ref={stageRef}
        width={size.w} height={size.h}
        scaleX={tr.s} scaleY={tr.s} x={tr.x} y={tr.y}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={endGesture} onMouseLeave={endGesture}
        onDblClick={onDblClick} onDblTap={onDblClick} onWheel={onWheel}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={endGesture}
        data-testid="floorplan-canvas-2d"
        style={{ cursor: tool === "select" ? "default" : "crosshair", background: brand.subtle, touchAction: "none" }}
      >
        <Layer listening={false}>
          {img && <KImage image={img} name="plan-bg" opacity={bgOpacity} />}
          {gridLines.map((l, i) => (
            <Line key={i} points={l.pts} stroke={brand.border} strokeWidth={(l.major ? 1 : 0.5) / tr.s}
                  opacity={l.major ? 0.9 : 0.45} />
          ))}
        </Layer>

        <Layer>
          {walls.map((w) => (
            <Group key={w.id}>
              {wallPieces(w).map((piece, pi) => {
                if (piece.pts) {
                  return (
                    <Line key={pi} points={piece.pts} stroke="#1E293B" strokeWidth={wallStroke}
                          lineCap="round" lineJoin="round"
                          onClick={() => tool === "select" && setSelected(w.id)}
                          onTap={() => tool === "select" && setSelected(w.id)}
                          opacity={selected === w.id ? 0.8 : 1} dash={selected === w.id ? [6 / tr.s, 4 / tr.s] : []} />
                  );
                }
                const { opening: o, ax, ay, ux, uy, s, e, deg } = piece;
                const isSel = selected === `op-${o.id}`;
                const gs = { x: ax + ux * s, y: ay + uy * s };   // gap start
                const ge = { x: ax + ux * e, y: ay + uy * e };   // gap end
                const nx = -uy, ny = ux;                          // perpendicular
                const wOff = wallStroke / 3;
                return (
                  <Group key={pi}
                    onClick={() => (tool === "select" || tool === "opening") && setSelected(`op-${o.id}`)}
                    onTap={() => (tool === "select" || tool === "opening") && setSelected(`op-${o.id}`)}>
                    {/* invisible fat hit area across the gap */}
                    <Line points={[gs.x, gs.y, ge.x, ge.y]} stroke="rgba(0,0,0,0)" strokeWidth={Math.max(o.wPx, 18 / tr.s)} />
                    {o.type === "door" ? (
                      <>
                        {/* quarter-circle door swing from the hinge at gap start */}
                        <Arc x={gs.x} y={gs.y} innerRadius={0} outerRadius={o.wPx}
                             angle={90} rotation={deg} fill="rgba(30,41,59,0.06)"
                             stroke={isSel ? "#3B82F6" : "#64748B"} strokeWidth={1.2 / tr.s} />
                        <Line points={[gs.x, gs.y, gs.x + nx * o.wPx * (deg > 90 || deg < -90 ? -1 : 1), gs.y + ny * o.wPx * (deg > 90 || deg < -90 ? -1 : 1)]}
                              stroke={isSel ? "#3B82F6" : "#64748B"} strokeWidth={1.2 / tr.s} />
                      </>
                    ) : (
                      <>
                        {/* window: two thin parallel lines across the gap */}
                        <Line points={[gs.x + nx * wOff, gs.y + ny * wOff, ge.x + nx * wOff, ge.y + ny * wOff]}
                              stroke={isSel ? "#3B82F6" : "#475569"} strokeWidth={1.4 / tr.s} />
                        <Line points={[gs.x - nx * wOff, gs.y - ny * wOff, ge.x - nx * wOff, ge.y - ny * wOff]}
                              stroke={isSel ? "#3B82F6" : "#475569"} strokeWidth={1.4 / tr.s} />
                      </>
                    )}
                  </Group>
                );
              })}
            </Group>
          ))}
          {tool === "wall" && draftWall.length >= 2 && (
            <Line points={mousePt ? [...draftWall, mousePt.x, mousePt.y] : draftWall}
                  stroke="#1E293B" strokeWidth={wallStroke} dash={[8 / tr.s, 5 / tr.s]} lineCap="round" />
          )}

          {items.map((it) => {
            const photo = itemImages[it.img || imgByRef?.[it.ref]] || null;
            // fit the product photo inside the footprint with a small inset
            let photoBox = null;
            if (photo) {
              const bw = it.wPx * 0.84, bh = it.dPx * 0.84;
              const ir = photo.width / photo.height;
              let dw = bw, dh = bw / ir;
              if (dh > bh) { dh = bh; dw = bh * ir; }
              photoBox = { dw, dh };
            }
            return (
            <Group
              key={it.uid}
              x={it.x} y={it.y} rotation={it.rot || 0}
              draggable={tool === "select"}
              onClick={() => setSelected(it.uid)}
              onTap={() => setSelected(it.uid)}
              onDragStart={() => setSelected(it.uid)}
              onDragEnd={(e) => setItems((arr) => arr.map((a) => a.uid === it.uid ? { ...a, x: e.target.x(), y: e.target.y() } : a))}
              onTransformEnd={(e) => setItems((arr) => arr.map((a) => a.uid === it.uid ? { ...a, rot: e.target.rotation(), x: e.target.x(), y: e.target.y() } : a))}
              ref={(r) => { if (r) shapeRefs.current[it.uid] = r; }}
            >
              {it.round ? (
                <Circle radius={it.wPx / 2} fill={it.color} opacity={photo ? 0.3 : 0.92}
                        stroke={colliding.has(it.uid) ? "#EF4444" : "rgba(0,0,0,0.25)"}
                        strokeWidth={(colliding.has(it.uid) ? 2.5 : 1) / tr.s} />
              ) : (
                <Rect x={-it.wPx / 2} y={-it.dPx / 2} width={it.wPx} height={it.dPx} fill={it.color} opacity={photo ? 0.3 : 0.92}
                      cornerRadius={Math.min(4 / tr.s, it.dPx / 6)}
                      stroke={colliding.has(it.uid) ? "#EF4444" : "rgba(0,0,0,0.25)"}
                      strokeWidth={(colliding.has(it.uid) ? 2.5 : 1) / tr.s} />
              )}
              {photo && (
                <KImage image={photo} x={-photoBox.dw / 2} y={-photoBox.dh / 2}
                        width={photoBox.dw} height={photoBox.dh} listening={false} />
              )}
              <KText
                text={mpp ? `${fmtM(it.width_m)} × ${fmtM(it.depth_m)}` : it.name.slice(0, 18)}
                fontSize={Math.max(10 / tr.s, 11)} fill="#111827"
                x={-it.wPx / 2} y={it.dPx / 2 + 3 / tr.s} width={it.wPx} align="center"
                listening={false}
              />
            </Group>
            );
          })}
          <Transformer ref={trRef} rotateEnabled resizeEnabled={false}
                       anchorSize={13 / tr.s} anchorCornerRadius={6 / tr.s} anchorStroke={brand.primary}
                       borderStroke={brand.primary} borderStrokeWidth={1.5 / tr.s} />

          {calibLine && (
            <>
              <Line points={calibLine.pts} stroke="#3B82F6" strokeWidth={2.5 / tr.s} lineCap="round" />
              <KText text={`${Math.round(calibLenPx)} px`} fontSize={Math.max(11 / tr.s, 12)} fill="#3B82F6"
                     x={(calibLine.pts[0] + calibLine.pts[2]) / 2 + 6 / tr.s}
                     y={(calibLine.pts[1] + calibLine.pts[3]) / 2 - 14 / tr.s} listening={false} />
            </>
          )}
        </Layer>
      </Stage>

      {/* Zoom controls — handy on desktop, essential on touch */}
      <div className="absolute top-3 right-3 flex flex-col shadow-lg z-10" style={{ background: brand.surface, border: `1px solid ${brand.border}` }} data-testid="zoom-controls">
        <button onClick={() => applyZoomAt(size.w / 2, size.h / 2, 1.3)} className={zoomBtn} style={{ color: brand.text }} title="Zoomer" data-testid="zoom-in-button">
          <Plus className="w-4 h-4" />
        </button>
        <button onClick={() => applyZoomAt(size.w / 2, size.h / 2, 1 / 1.3)} className={zoomBtn} style={{ color: brand.text, borderTop: `1px solid ${brand.border}` }} title="Dézoomer" data-testid="zoom-out-button">
          <Minus className="w-4 h-4" />
        </button>
        <button onClick={() => setZoom({ s: 1, x: 0, y: 0 })} className={zoomBtn} style={{ color: brand.text, borderTop: `1px solid ${brand.border}` }} title="Recentrer le plan" data-testid="zoom-reset-button">
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>
    </>
  );
}
